<script lang="ts">
  import { tick } from "svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import type { PopoverAlignmentOptions } from "@shared/types/PopoverWithButton";
  import type { Writable } from "svelte/store";
  import type { Field } from "svelte-forms2/types";

  type FormField = Writable<Field<string>>;

  export let inputId: string = "";
  export let popoverClass: string = "";
  export let inverted: boolean = false;
  export let show: boolean = false;
  export let arrow: boolean = true;

  export let buttonDisabled: boolean = false;
  export let buttonLabel: string;
  export let openPopover: any;
  export let minWidth: string = "none";
  export let position: string;

  export let icon: { [key: string]: string };
  export let customButtonStyle: string = "";

  export let selectButtonStyle: boolean = false;
  export let objectName: string = "";
  export let extraLabelClasses: string = "";
  export let field: FormField = undefined;

  export let alignmentOptions: PopoverAlignmentOptions = {
    desktop: { xPosition: "right" },
    mobile: { xPosition: "left" },
  };

  export let onFocus: () => void = null;

  const WINDOW_PADDING = 40;

  let positionRef: HTMLElement | null;
  let popoverRef: HTMLElement | null;
  let popoverArrowRef: HTMLElement | null;
  let popoverArrowPositionRef: HTMLElement | null;

  async function prepareForShow() {
    var positioner = positionRef;
    if (!positioner) {
      return;
    }
    await tick();

    let popover = popoverRef;
    let popoverArrowPositioner = popoverArrowPositionRef;
    var popoverPosition = popover.getBoundingClientRect();

    // Position popover body
    if (popoverPosition.left < WINDOW_PADDING) {
      positioner.style.position = "relative";
      positioner.style.left = WINDOW_PADDING - popoverPosition.left + "px";

      if (arrow) {
        popoverArrowPositioner.style.position = "relative";
        popoverArrowPositioner.style.left = -(WINDOW_PADDING - popoverPosition.left) + "px";
      }
    } else if (popoverPosition.right > window.innerWidth - WINDOW_PADDING) {
      positioner.style.position = "relative";
      positioner.style.left = window.innerWidth - WINDOW_PADDING - popoverPosition.right + "px";

      if (arrow) {
        popoverArrowPositioner.style.position = "relative";
        popoverArrowPositioner.style.left =
          -(window.innerWidth - WINDOW_PADDING - popoverPosition.right) + "px";
      }
    }
  }

  function unprepare() {
    var positioner = positionRef;
    if (!positioner) {
      return;
    }
    positioner.style.transform = "";
  }

  $: {
    if (show) {
      prepareForShow();
    } else {
      unprepare();
    }
  }

  function clickOutside(node: HTMLElement) {
    const handleClick = function ({ target }) {
      if (!node.contains(target) && !(target.nodeName == "A")) {
        window["closeTimer"] = setTimeout(function () {
          if (show) {
            show = false;
          }
        }, 100);
      } else {
        setTimeout(function () {
          if (window["closeTimer"]) {
            clearTimeout(window["closeTimer"]);
          }
        }, 10);
      }
    };

    setTimeout(function () {
      window.addEventListener("click", handleClick);
    }, 200);

    return {
      destroy() {
        window.removeEventListener("click", handleClick);
      },
    };
  }

</script>

<style lang="scss">
  .custom-popover {
    position: absolute;
    z-index: 300;
    top: 0.25rem;
  }

  .popover-container {
    position: relative;
    height: 0;
    width: inherit;
  }

  @media (max-width: 699px) {
    .mobile-position-right {
      right: 0;
    }

    .mobile-position-left {
      left: 0;
    }
  }

  @media (min-width: 700px) {
    .desktop-position-right {
      right: 0;
    }

    .desktop-position-left {
      left: 0;
    }
  }

  .daterangepicker-button:disabled {
    background: #eaeaea;
    cursor: not-allowed;
  }

</style>

<div style="min-width: {minWidth};">
  <button
    on:click={openPopover}
    id={inputId}
    class="daterangepicker-button fizz-full-width"
    class:fizz-button-select={selectButtonStyle}
    class:fizz-button={!selectButtonStyle}
    data-testid={objectName}
    disabled={buttonDisabled}
    style={customButtonStyle}
    class:fizz-input-error={field && !$field?.valid}
    on:focus={onFocus}
  >
    {#if selectButtonStyle}
      <label for="popover-with-button" class={extraLabelClasses}>{buttonLabel}</label>
    {:else}
      <span class="fizz-space-between">
        <span>{buttonLabel}</span>
        <Icon type={icon.type} height={icon.height} width={icon.width} />
      </span>
    {/if}
  </button>
  <div class="popover-container">
    {#if show}
      <div
        class="custom-popover fizz-surface fizz-elevation-1 fizz-radius-medium {popoverClass}"
        class:popover--inverted={inverted}
        class:desktop-position-left={alignmentOptions.desktop.xPosition == "left"}
        class:desktop-position-right={alignmentOptions.desktop.xPosition == "right"}
        class:mobile-position-left={alignmentOptions.mobile.xPosition == "left"}
        class:mobile-position-right={alignmentOptions.mobile.xPosition == "right"}
        bind:this={popoverRef}
        use:clickOutside
        style={position}
      >
        {#if arrow}
          <div class="popover__arrow-position" bind:this={popoverArrowPositionRef}>
            <div class="popover__arrow" bind:this={popoverArrowRef} />
          </div>
        {/if}
        <slot />
      </div>
    {/if}
  </div>
</div>
